import React, { useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Box, Stack, Typography, Popover } from "@mui/material";
import "./customCalendar.css";
import BookingList from "./BookingList";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Bookings from "./Bookings";

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const CalendarBooking = ({
  availabilityDocuments,
  isColor,
  isList,
  activeMonth = new Date(),
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const handleDateClick = (date) => {
    setSelectedDate(date);
  };
  const handleOpenPopover = (event, booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };
  const handleClosePopover = () => {
    setAnchorEl(null);
    setSelectedBooking(null);
  };
  const getTileClassName = ({ date, view }) => {
    if (view !== "month") return null;
    const formattedDate = formatDate(date);
    const today = new Date(); // Ottieni la data corrente
    today.setHours(0, 0, 0, 0); // Imposta l'ora a mezzanotte per evitare problemi di confronto con ore/minuti/secondi
    const roomBookings = availabilityDocuments.filter((booking) => {
      const checkInDate = new Date(booking.startDate.seconds * 1000);
      const checkOutDate = new Date(booking.endDate.seconds * 1000);
      const adjustedCheckOutDate = new Date(checkOutDate.getTime() - 86400000);
      return (
        formatDate(checkInDate) <= formattedDate &&
        formatDate(adjustedCheckOutDate) >= formattedDate
      );
    });
    const isFree = roomBookings.length === 0;
    if (date < today) {
      return "past-day"; // Classe per i giorni passati
    }
    if (isColor) {
      if (roomBookings.some((booking) => booking.isManual)) {
        return "manual-day"; // Casella gialla
      }
      if (roomBookings.some((booking) => booking.isWebsite)) {
        return "website-day"; // Casella blu
      }
      return isFree ? "free-day" : "occupied-day"; // Casella verde o rossa
    }
    return null; // Nessuna classe se isColor è false
  };

  const getTileContent = ({ date, view }) => {
    if (view !== "month") return null;

    const formattedDate = formatDate(date);

    // Filtra le prenotazioni per la data corrente
    const roomBookings = availabilityDocuments.filter(
      (booking) =>
        formatDate(new Date(booking.startDate.seconds * 1000)) <=
          formattedDate &&
        formatDate(new Date(booking.endDate.seconds * 1000)) >= formattedDate
    );

    // Trova tutte le prenotazioni di check-in per questa data
    const checkInBookings = availabilityDocuments.filter(
      (booking) =>
        formatDate(new Date(booking.startDate.seconds * 1000)) === formattedDate
    );
    // Prioritizza le prenotazioni del sito web, poi quelle manuali
    const checkInBooking =
      checkInBookings.find((b) => b.isWebsite) ||
      checkInBookings.find((b) => b.isManual) ||
      checkInBookings[0];

    // Trova tutte le prenotazioni di check-out per questa data
    const checkOutBookings = availabilityDocuments.filter(
      (booking) =>
        formatDate(new Date(booking.endDate.seconds * 1000)) === formattedDate
    );
    // Prioritizza le prenotazioni del sito web, poi quelle manuali
    const checkOutBooking =
      checkOutBookings.find((b) => b.isWebsite) ||
      checkOutBookings.find((b) => b.isManual) ||
      checkOutBookings[0];

    return (
      <div style={{ marginTop: "5px" }}>
        {!isColor && (
          <>
            {/* Mostra il pallino azzurro per il check-in */}
            {checkInBooking && (
              <span
                style={
                  checkInBooking.isWebsite
                    ? styles.checkInDot // Azzurro se è un booking da sito
                    : styles.checkInDotBooking // Giallo (o invisibile) se diverso
                }
              ></span>
            )}

            {/* Mostra il pallino grigio per il check-out */}
            {checkOutBooking && (
              <span
                style={
                  checkOutBooking.isWebsite
                    ? styles.checkOutDot // Grigio se è un booking da sito
                    : styles.checkInDotBooking // Giallo (o invisibile) se diverso
                }
              ></span>
            )}
          </>
        )}

        {isColor && (
          <span
            style={
              roomBookings.length === 0
                ? styles.freeDot // Nessuna prenotazione: verde
                : styles.occupiedDot // Almeno una prenotazione: giallo
            }
          ></span>
        )}
      </div>
    );
  };

  const classifyBookings = () => {
    if (!selectedDate) return { checkIn: [], inProgress: [], checkOut: [] };
    const formattedDate = formatDate(selectedDate);
    const checkIn = [];
    const inProgress = [];
    const checkOut = [];

    availabilityDocuments.forEach((booking) => {
      const startDate = formatDate(new Date(booking.startDate.seconds * 1000));
      const endDate = formatDate(new Date(booking.endDate.seconds * 1000));

      if (startDate === formattedDate) {
        checkIn.push(booking);
      } else if (endDate === formattedDate) {
        checkOut.push(booking);
      } else if (startDate < formattedDate && endDate > formattedDate) {
        inProgress.push(booking);
      }
    });

    return { checkIn, inProgress, checkOut };
  };

  const { checkIn, inProgress, checkOut } = classifyBookings();

  return (
    <div style={styles.page}>
      <h2
        style={{
          display: isColor || isList ? "none" : "flex",
        }}
      >
        Calendario
      </h2>
      <Calendar
        showNavigation={isColor ? false : true}
        onClickDay={handleDateClick}
        tileContent={getTileContent}
        activeStartDate={isColor ? activeMonth : undefined} // Usa il mese sincronizzato
        tileClassName={getTileClassName} // Usa la classe condizionata
        className={isColor ? "compact-calendar" : isList ? "list-calendar" : ""} // Aggiungi la classe condizionata
      />
      {/* LISTA PRENOTAZIONI */}
      {isColor ? null : (
        <Stack
          width={"100%"}
          justifyContent={"center"}
          sx={{
            margin: isList ? 0 : "20px",
            backgroundColor: isList ? "white" : "rgba(45, 45, 45, 0.8)",
            backdropFilter: "blur(10px)",
            borderRadius: "20px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            gap: isList ? 0 : "10px",
            p: 1,
          }}
        >
          <Stack
            flexDirection={"row"}
            width={"100%"}
            display={isList ? "none" : "flex"}
            justifyContent={"center"}
            sx={{
              borderRadius: "10px",
              pt: "10px",
              gap: "15px",
            }}
          >
            <Box p={"10px 25px"} sx={styles.checkInCard}>
              <Typography
                variant="body1"
                sx={{ color: "white", fontSize: "0.9rem", opacity: 0.9, mb: 1 }}
              >
                Check-in
              </Typography>
              <Typography
                textAlign={"center"}
                variant="h4"
                sx={{ color: "white", fontSize: "2rem", fontWeight: "bold" }}
              >
                {checkIn.length}
              </Typography>
            </Box>
            <Box p={"10px 25px"} sx={styles.inProgressCard}>
              <Typography
                variant="body1"
                sx={{ color: "white", fontSize: "0.9rem", opacity: 0.9, mb: 1 }}
              >
                In corso
              </Typography>
              <Typography
                textAlign={"center"}
                variant="h4"
                sx={{ color: "white", fontSize: "2rem", fontWeight: "bold" }}
              >
                {inProgress.length}
              </Typography>
            </Box>
            <Box p={"10px 25px"} sx={styles.checkOutCard}>
              <Typography
                variant="body1"
                sx={{ color: "white", fontSize: "0.9rem", opacity: 0.9, mb: 1 }}
              >
                Check-out
              </Typography>
              <Typography
                textAlign={"center"}
                variant="h4"
                sx={{ color: "white", fontSize: "2rem", fontWeight: "bold" }}
              >
                {checkOut.length}
              </Typography>
            </Box>
          </Stack>
          <Stack
            spacing={2}
            width={"100%"}
            display={isList ? "none" : undefined}
            justifyContent={"center"}
            alignItems={"space-between"}
            pb={3}
          >
            <Box p={1}>
              <BookingList
                bookings={checkIn}
                onBookingClick={handleOpenPopover}
                title="Check-in"
              />
              <BookingList
                bookings={inProgress}
                onBookingClick={handleOpenPopover}
                title="In corso"
              />
              <BookingList
                bookings={checkOut}
                onBookingClick={handleOpenPopover}
                title="Check-out"
              />
            </Box>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleClosePopover}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                sx: {
                  width: "100vw", // Larghezza completa della viewport
                  height: "70vh", // Altezza completa della viewport
                  margin: 0, // Rimuove i margini
                  padding: 0, // Rimuove il padding
                  position: "relative", // Necessario per posizionare la X
                },
              }}
            >
              {selectedBooking && (
                <Box
                  p={3}
                  sx={{
                    position: "relative",
                    height: "100%",

                    color: "#fff",
                    borderRadius: 2,
                  }}
                >
                  {/* Icona di chiusura in alto a destra */}
                  <IconButton
                    onClick={handleClosePopover}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: "#fff", // Imposta il colore dell'icona
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
                    Dettagli prenotazione
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Nome: <strong>{selectedBooking.customerName}</strong>
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Cellulare: <strong>{selectedBooking.phoneNumber}</strong>
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Email: <strong>{selectedBooking.email}</strong>
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Data di check-in:{" "}
                    <strong>
                      {new Date(
                        selectedBooking.startDate.seconds * 1000
                      ).toLocaleString()}
                    </strong>
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Data di check-out:{" "}
                    <strong>
                      {new Date(
                        selectedBooking.endDate.seconds * 1000
                      ).toLocaleString()}
                    </strong>
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Prezzo: <strong>{selectedBooking.price}€</strong>
                  </Typography>
                </Box>
              )}
            </Popover>
          </Stack>
          {isList && (
            <Bookings
              isList={isList}
              availabilityDocuments={availabilityDocuments}
              isPrice={false}
            />
          )}
        </Stack>
      )}
    </div>
  );
};

const styles = {
  page: {
    backgroundColor: "#121212",
    color: "white",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  checkInCard: {
    background: "linear-gradient(135deg, #ab9501 0%, #d4af37 100%)",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(171, 149, 1, 0.3)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 32px rgba(171, 149, 1, 0.4)",
    },
  },
  inProgressCard: {
    background: "linear-gradient(135deg, #6d6d6d 0%, #8a8a8a 100%)",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(109, 109, 109, 0.3)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 32px rgba(109, 109, 109, 0.4)",
    },
  },
  checkOutCard: {
    background: "linear-gradient(135deg, #ff3939 0%, #ff6b6b 100%)",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(255, 57, 57, 0.3)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 32px rgba(255, 57, 57, 0.4)",
    },
  },
  freeDot: {
    display: "inline-block",
    width: "10px",
    height: "10px",
    backgroundColor: "green",
    borderRadius: "50%",
    margin: "0 3px",
  },
  occupiedDot: {
    display: "inline-block",
    width: "10px",
    height: "10px",
    backgroundColor: "yellow",
    borderRadius: "50%",
    margin: "0 3px",
  },
  checkInDot: {
    display: "inline-block",
    width: "10px",
    height: "10px",
    backgroundColor: "#35ccff",
    borderRadius: "50%",
    margin: "0 3px",
  },
  checkInDotBooking: {
    display: "none",
    width: "10px",
    height: "10px",
    backgroundColor: "#fcff43",
    borderRadius: "50%",
    margin: "0 3px",
  },
  checkOutDot: {
    display: "inline-block",
    width: "10px",
    height: "10px",
    backgroundColor: "#aaaaaa",
    borderRadius: "50%",
    margin: "0 3px",
  },
};

export default CalendarBooking;
